import "./App.css";
import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "@fontsource/inter";
import VendorOnboarding from "./components/VendorOnboardingAndLegalApproval/VendorOnboarding";
import AdminLayout from "./components/AdminLayout";
import SignIn from "./views/SignIn";
import PrivacyPolicy from "./views/PrivacyPolicy";
// import SignUp from "./views/SignUp";
import HealthDashboardView from "./views/HealthDashboardView";
import VendorsAvailabilityView from "./views/VendorsAvailabilityView";
import UserAnalyticsView from "./views/UserAnalyticsView";
import DeliveryHealthView from "./views/DeliveryHealthView";
import DigitalWalletView from "./views/DigitalWalletView";
import ComplaintCenterView from "./views/ComplaintCenterView";
import SystemOrdersView from "./views/SystemOrdersView";
import WishPay from "./views/WishPay";
// import ButlerFulfillmentView from "./views/ButlerFulfillmentView";
import VendorDashboard from "./vendorFlow/VendorDashboard";
import VendorMenu from "./vendorFlow/VendorMenu";
import VendorAddItem from "./vendorFlow/VendorAddItem";
import VendorOrders from "./vendorFlow/VendorOrders";
import VendorPromotions from "./vendorFlow/VendorPromotions";
import VendorStorefront from "./vendorFlow/VendorStorefront";
import VendorAccountSettings from "./vendorFlow/VendorAccountSettings";
import UserManagement from "./vendorFlow/UserManagement";
import VendorAddUser from "./vendorFlow/VendorAddUser";
import VendorEditUser from "./vendorFlow/VendorEditUser";
import VendorRoleForm from "./vendorFlow/VendorRoleForm";
import VendorLayout from "./vendorFlow/VendorLayout";
import VendorLoadingOverlay from "./vendorFlow/VendorLoadingOverlay";
import VendorApprovalStatusBanner from "./vendorFlow/VendorApprovalStatusBanner";
import {
  BusinessTypeSelection,
  DeliveryAccount,
  DeliveryDocuments,
  DeliveryRegistrationSuccess,
  DeliveryTerms,
  DeliveryType,
  DeliveryVerify,
  DocumentTerms,
  RegistrationStart,
  RegistrationSuccess,
  VendorAccount,
  VendorCategoryDetails,
  VendorDocuments,
  VendorTerms,
  VendorVerify,
} from "./registrationFlow/RegistrationFlow";
import ResubmitCompanyAttachments from "./registrationFlow/ResubmitCompanyAttachments";
import RegistrationAddress from "./registrationFlow/RegistrationAddress";
import { userService } from "./services/userService";
import {
  hasAdminPortalAccess,
  hasVendorPortalAccess,
} from "./vendorFlow/access";
import { translateLabel, useTranslationSnapshot } from "./translations/runtime";


function VendorAccessRoute({ children }) {
  useTranslationSnapshot();
  const storedUser = userService.getJwt();
  const payload = userService.getUserPayload();

  if (!storedUser || !payload) {
    return <Navigate to="/sign-in" replace />;
  }

  if (!hasVendorPortalAccess(payload)) {
    return <Navigate to="/vendor-onboarding" replace />;
  }

  return children;
}

function AdminAccessRoute({ children }) {
  useTranslationSnapshot();
  const storedUser = userService.getJwt();
  const payload = userService.getUserPayload();

  if (!storedUser || !payload) {
    return <Navigate to="/sign-in" replace />;
  }

  if (!hasAdminPortalAccess(payload)) {
    return <Navigate to="/vendor-dashboard" replace />;
  }

  return children;
}

function App() {
  useTranslationSnapshot();
  const location = useLocation();
  const [showInitialLoader, setShowInitialLoader] = useState(true);
  const [apiLoaderCount, setApiLoaderCount] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const minimumDuration = 900;

    const hideLoader = () => {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(minimumDuration - elapsed, 0);

      window.setTimeout(() => {
        setShowInitialLoader(false);
      }, remaining);
    };

    if (document.readyState === "complete") {
      hideLoader();
      return undefined;
    }

    window.addEventListener("load", hideLoader, { once: true });

    return () => {
      window.removeEventListener("load", hideLoader);
    };
  }, []);

  useEffect(() => {
    setShowInitialLoader(true);

    const timerId = window.setTimeout(() => {
      setShowInitialLoader(false);
    }, 650);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    const startLoader = () => {
      setApiLoaderCount((currentCount) => currentCount + 1);
    };
    const stopLoader = () => {
      setApiLoaderCount((currentCount) => Math.max(currentCount - 1, 0));
    };

    window.addEventListener("vendor-loading:start", startLoader);
    window.addEventListener("vendor-loading:stop", stopLoader);

    return () => {
      window.removeEventListener("vendor-loading:start", startLoader);
      window.removeEventListener("vendor-loading:stop", stopLoader);
    };
  }, []);

  useEffect(() => {
    const refreshStoredJwt = () => {
      userService.refreshJwt().catch(() => {});
    };

    refreshStoredJwt();

    const intervalId = window.setInterval(refreshStoredJwt, 3 * 60 * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <div className="App">
      <VendorLoadingOverlay
        label={translateLabel("txt_end_user_loading", "Loading")}
        visible={showInitialLoader || apiLoaderCount > 0}
      />
      <ToastContainer />
      <VendorApprovalStatusBanner />
      <Routes>
        <Route path="/" element={<Navigate to="/sign-in" replace />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/register" element={<RegistrationStart />} />
        <Route path="/register/business-type" element={<BusinessTypeSelection />} />
        <Route path="/register/vendor/category" element={<VendorCategoryDetails />} />
        <Route path="/register/vendor/account" element={<VendorAccount />} />
        <Route path="/register/vendor/terms" element={<VendorTerms />} />
        <Route path="/register/vendor/verify" element={<VendorVerify />} />
        <Route path="/register/vendor/documents" element={<VendorDocuments />} />
        <Route path="/register/documents/terms" element={<DocumentTerms />} />
        <Route path="/register/success" element={<RegistrationSuccess />} />
        <Route path="/register/address" element={<RegistrationAddress />} />
        <Route path="/register/delivery/account" element={<DeliveryAccount />} />
        <Route path="/register/delivery/terms" element={<DeliveryTerms />} />
        <Route path="/register/delivery/verify" element={<DeliveryVerify />} />
        <Route path="/register/delivery/type" element={<DeliveryType />} />
        <Route path="/register/delivery/documents" element={<DeliveryDocuments />} />
        <Route path="/register/delivery/success" element={<DeliveryRegistrationSuccess />} />
        <Route path="/register/delivery/address" element={<RegistrationAddress mode="delivery" />} />
        <Route
          path="/resubmit-company-attachments/*"
          element={<ResubmitCompanyAttachments />}
        />
        {/* <Route path="/sign-up" element={<SignUp />} /> */}
        <Route path="/vendor-onboarding" element={
          <AdminAccessRoute>
            <AdminLayout activeKey="business-request">
              <VendorOnboarding />
            </AdminLayout>
          </AdminAccessRoute>
        } />
        <Route path="/health-dashboard" element={
          <AdminAccessRoute>
            <AdminLayout activeKey="health-dashboard">
              <HealthDashboardView />
            </AdminLayout>
          </AdminAccessRoute>
        } />
        <Route path="/vendors" element={
          <AdminAccessRoute>
            <AdminLayout activeKey="vendors">
              <VendorsAvailabilityView />
            </AdminLayout>
          </AdminAccessRoute>
        } />
        <Route path="/user-analytics" element={
          <AdminAccessRoute>
            <AdminLayout activeKey="user-analytics">
              <UserAnalyticsView />
            </AdminLayout>
          </AdminAccessRoute>
        } />
        <Route path="/delivery-health" element={
          <AdminAccessRoute>
            <AdminLayout activeKey="delivery-health">
              <DeliveryHealthView />
            </AdminLayout>
          </AdminAccessRoute>
        } />
        <Route path="/digital-wallet" element={
          <AdminAccessRoute>
            <AdminLayout activeKey="wallet">
              <DigitalWalletView />
            </AdminLayout>
          </AdminAccessRoute>
        } />
        <Route path="/complaints" element={
          <AdminAccessRoute>
            <AdminLayout activeKey="complaints">
              <ComplaintCenterView />
            </AdminLayout>
          </AdminAccessRoute>
        } />
        <Route path="/orders" element={
          <AdminAccessRoute>
            <AdminLayout activeKey="orders">
              <SystemOrdersView />
            </AdminLayout>
          </AdminAccessRoute>
        } />
        <Route path="/wishpay" element={<WishPay />} />
        {/*
        <Route path="/butler" element={
          <AdminLayout activeKey="butler">
            <ButlerFulfillmentView />
          </AdminLayout>
        } />
        */}
        <Route
          path="/vendor-dashboard"
          element={
            <VendorAccessRoute>
              <VendorDashboard />
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-menu"
          element={
            <VendorAccessRoute>
              <VendorMenu />
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-menu/add"
          element={
            <VendorAccessRoute>
              <VendorAddItem />
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-menu/edit/:itemId"
          element={
            <VendorAccessRoute>
              <VendorAddItem />
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-promotions"
          element={
            <VendorAccessRoute>
              <VendorPromotions />
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-orders"
          element={
            <VendorAccessRoute>
              <VendorOrders />
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-storefront"
          element={
            <VendorAccessRoute>
              <VendorStorefront />
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-user-management"
          element={
            <VendorAccessRoute>
              <UserManagement />
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-user-management/add"
          element={
            <VendorAccessRoute>
              <VendorAddUser />
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-user-management/edit/:userId"
          element={
            <VendorAccessRoute>
              <VendorEditUser />
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-user-management/roles/add"
          element={
            <VendorAccessRoute>
              <VendorRoleForm mode="add" />
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-user-management/roles/edit/:roleId"
          element={
            <VendorAccessRoute>
              <VendorRoleForm mode="edit" />
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-digital-wallet"
          element={
            <VendorAccessRoute>
              <VendorLayout activeKey="wallet">
                <DigitalWalletView />
              </VendorLayout>
            </VendorAccessRoute>
          }
        />
        <Route
          path="/vendor-account-settings"
          element={
            <VendorAccessRoute>
              <VendorAccountSettings />
            </VendorAccessRoute>
          }
        />
        {process.env.NODE_ENV === "development" && (
          <>
            <Route
              path="/vendor-dashboard-preview"
              element={<VendorDashboard />}
            />
            <Route path="/vendor-menu-preview" element={<VendorMenu />} />
            <Route path="/vendor-orders-preview" element={<VendorOrders />} />
            <Route
              path="/vendor-storefront-preview"
              element={<VendorStorefront />}
            />
            <Route
              path="/vendor-menu-add-preview"
              element={<VendorAddItem />}
            />
            <Route
              path="/vendor-menu-edit-preview/:itemId"
              element={<VendorAddItem />}
            />
            <Route
              path="/vendor-promotions-preview"
              element={<VendorPromotions />}
            />
            <Route
              path="/vendor-user-management-preview"
              element={<UserManagement />}
            />
            <Route
              path="/vendor-add-user-preview"
              element={<VendorAddUser />}
            />
            <Route
              path="/vendor-edit-user-preview/:userId"
              element={<VendorEditUser />}
            />
            <Route
              path="/vendor-add-role-preview"
              element={<VendorRoleForm mode="add" />}
            />
            <Route
              path="/vendor-edit-role-preview/:roleId"
              element={<VendorRoleForm mode="edit" />}
            />
            <Route
              path="/vendor-digital-wallet-preview"
              element={
                <VendorLayout activeKey="wallet">
                  <DigitalWalletView />
                </VendorLayout>
              }
            />
            <Route
              path="/vendor-account-settings-preview"
              element={<VendorAccountSettings />}
            />
          </>
        )}
      </Routes>
    </div>
  );
}

export default App;
